import { useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import { createPortal } from "react-dom";
import {
  ArrowRight,
  Loader2,
  LockKeyhole,
  MapPin,
  Search,
  Store,
  X,
} from "lucide-react";

import {
  getPublicDealPreview,
  type PublicDealPreview,
  type PublicPreviewDeal,
} from "../services/PublicPreviewService";
import { track } from "../utils/analytics";
import { getProductImageUrl } from "../utils/imageUtils";

const APPAREL_WORDS = /\b(shoe|shoes|sneaker|sneakers|shirt|top|blouse|dress|skirt|shorts|pants|jeans|legging|leggings|bra|underwear|swimsuit|jacket|coat|hoodie|sweater|clothing|apparel)\b/i;

const DISCOUNT_RANK: Record<PublicPreviewDeal["discountBand"], number> = {
  "25%+ off": 25,
  "50%+ off": 50,
  "70%+ off": 70,
};

export function selectCompellingNearbyDeals(deals: PublicPreviewDeal[], limit = 3) {
  const ranked = [...deals].sort(
    (left, right) => DISCOUNT_RANK[right.discountBand] - DISCOUNT_RANK[left.discountBand],
  );
  const preferred = ranked.filter((deal) => !APPAREL_WORDS.test(deal.name));
  return (preferred.length > 0 ? preferred : ranked).slice(0, limit);
}

function DealImage({ deal, compact = false }: { deal: PublicPreviewDeal; compact?: boolean }) {
  const image = getProductImageUrl(deal.image, "300");
  return image ? (
    <img
      src={image}
      alt={deal.name}
      className={compact ? "h-20 w-20 object-contain p-1" : "h-full w-full object-contain p-1"}
    />
  ) : (
    <div className={`flex items-center justify-center text-violet-300 ${compact ? "h-20 w-20" : "h-full w-full"}`} aria-hidden="true">
      <Store className={compact ? "h-9 w-9" : "h-5 w-5"} strokeWidth={1.5} />
    </div>
  );
}

export default function NearbySavingsHero({
  initialZip,
  audience = "free",
  onSeePlans,
}: {
  initialZip?: string | null;
  audience?: "guest" | "free";
  onSeePlans: (zipCode: string) => void;
}) {
  const validInitialZip = /^\d{5}$/.test(initialZip || "") ? initialZip! : "";
  const [zip, setZip] = useState(validInitialZip);
  const [submittedZip, setSubmittedZip] = useState(validInitialZip);
  const [preview, setPreview] = useState<PublicDealPreview | null>(null);
  const [loading, setLoading] = useState(Boolean(validInitialZip));
  const [error, setError] = useState("");
  const [requestVersion, setRequestVersion] = useState(0);
  const [resultsOpen, setResultsOpen] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!submittedZip) return;
    let active = true;
    getPublicDealPreview(submittedZip)
      .then((result) => {
        if (!active) return;
        setPreview(result);
        track("guest_local_preview_started", {
          surface: "web",
          retailer: "walmart",
          trigger: "nearby_savings_hero",
          source: audience === "guest" ? "public_feed" : "free_feed",
        });
      })
      .catch((caught: unknown) => {
        if (!active) return;
        setPreview(null);
        setError(caught instanceof Error ? caught.message : "We could not scan that area right now.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [audience, requestVersion, submittedZip]);

  useEffect(() => {
    if (!resultsOpen) return;
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setResultsOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [resultsOpen]);

  const heroDeals = useMemo(
    () => selectCompellingNearbyDeals(preview?.deals || []),
    [preview?.deals],
  );
  const bestDiscount = heroDeals[0]?.discountBand;
  const dealCount = preview?.deals.length || 0;

  const submitZip = (event: FormEvent) => {
    event.preventDefault();
    if (!/^\d{5}$/.test(zip)) {
      setError("Enter a valid 5-digit ZIP code.");
      return;
    }
    setLoading(true);
    setError("");
    setSubmittedZip(zip);
    setRequestVersion((version) => version + 1);
  };

  return (
    <>
      <section className="mx-auto w-full max-w-5xl px-3 pb-2 pt-3 sm:px-5 sm:pb-3">
        <div className="flex min-h-[86px] items-center gap-2 overflow-hidden rounded-[22px] border border-violet-100 bg-[#f8f4ff] px-3 py-2.5 shadow-sm shadow-violet-950/[0.04] dark:border-violet-500/20 dark:bg-[#21182b] sm:gap-3 sm:px-4">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white text-violet-600 shadow-sm dark:bg-violet-500/15 dark:text-violet-300">
            <MapPin className="h-5 w-5" strokeWidth={2.25} aria-hidden="true" />
          </div>

          {preview && dealCount > 0 ? (
            <>
              <div className="min-w-0 flex-1">
                <h2 className="truncate text-[15px] font-black leading-5 tracking-[-0.02em] text-[#211238] dark:text-white sm:text-lg">
                  Nearby deals {bestDiscount || "spotted"}
                </h2>
                <p className="truncate text-[11px] font-semibold leading-4 text-stone-500 dark:text-stone-300 sm:text-xs">
                  Real clearance near {preview.zipCode}
                </p>
              </div>

              <div className="flex shrink-0 -space-x-2.5" aria-label="Real nearby product preview">
                {heroDeals.map((deal, index) => (
                  <div
                    key={`${deal.name}-${index}`}
                    className={`h-10 w-10 overflow-hidden rounded-lg border-2 border-[#f8f4ff] bg-white shadow-sm dark:border-[#21182b] ${index === 2 ? "hidden min-[430px]:block" : ""}`}
                    title={deal.name}
                  >
                    <DealImage deal={deal} />
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={() => setResultsOpen(true)}
                aria-label={`Preview all ${dealCount} nearby deals`}
                className="inline-flex h-10 shrink-0 items-center justify-center rounded-xl bg-violet-600 px-3 text-xs font-black text-white shadow-sm shadow-violet-600/20 transition hover:bg-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#21182b] sm:px-4 sm:text-sm"
              >
                Preview
              </button>
            </>
          ) : (
            <>
              <div className="min-w-0 flex-1">
                <h2 className="truncate text-[15px] font-black leading-5 tracking-[-0.02em] text-[#211238] dark:text-white sm:text-lg">
                  {loading ? "Checking nearby deals…" : "Preview nearby clearance"}
                </h2>
                <p className={`truncate text-[11px] font-semibold leading-4 sm:text-xs ${error ? "text-rose-600 dark:text-rose-300" : "text-stone-500 dark:text-stone-300"}`} role={error ? "alert" : undefined}>
                  {error || "Enter your ZIP to see real local products"}
                </p>
              </div>
              <form onSubmit={submitZip} className="flex shrink-0 items-center gap-1.5">
                <label>
                  <span className="sr-only">ZIP code</span>
                  <input
                    value={zip}
                    onChange={(event) => setZip(event.target.value.replace(/\D/g, "").slice(0, 5))}
                    inputMode="numeric"
                    placeholder="ZIP"
                    aria-label="ZIP code"
                    className="h-10 w-[70px] rounded-xl border border-violet-200 bg-white px-2 text-center text-sm font-bold text-[#211238] outline-none transition placeholder:text-stone-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 dark:border-violet-500/25 dark:bg-stone-950 dark:text-white sm:w-24"
                  />
                </label>
                <button type="submit" disabled={loading} aria-label="Scan nearby clearance" className="grid h-10 w-10 place-items-center rounded-xl bg-violet-600 text-white transition hover:bg-violet-700 disabled:opacity-60">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </button>
              </form>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-1 pb-1 pt-4">
          <h2 className="text-xl font-black tracking-tight text-[#211238] dark:text-white sm:text-2xl">Free online deals</h2>
          <span className="shrink-0 text-xs font-black text-violet-700 dark:text-violet-300">Always free</span>
        </div>
      </section>

      {preview && resultsOpen && typeof document !== "undefined" && createPortal(
        <div className="fixed inset-0 z-[10050] flex items-end justify-center bg-stone-950/70 backdrop-blur-sm sm:items-center sm:p-5 sheet-backdrop-in" onMouseDown={() => setResultsOpen(false)}>
          <section className="flex max-h-[88dvh] w-full max-w-2xl flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl dark:bg-stone-950 sm:rounded-3xl sheet-panel-in-sm" role="dialog" aria-modal="true" aria-labelledby="nearby-results-title" onMouseDown={(event) => event.stopPropagation()}>
            <header className="flex items-start justify-between gap-3 border-b border-stone-100 px-4 py-4 dark:border-stone-800">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.14em] text-violet-700 dark:text-violet-300">Clearance near {preview.zipCode}</p>
                <h2 id="nearby-results-title" className="mt-1 text-xl font-black text-[#211238] dark:text-white">{dealCount} nearby {dealCount === 1 ? "find" : "finds"}</h2>
              </div>
              <button ref={closeRef} type="button" onClick={() => setResultsOpen(false)} className="grid h-10 w-10 place-items-center rounded-full bg-stone-100 text-stone-600 dark:bg-stone-900 dark:text-stone-300" aria-label="Close nearby preview"><X className="h-4 w-4" /></button>
            </header>
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
              {preview.deals.map((deal, index) => (
                <article key={`${deal.name}-${index}`} className="flex items-center gap-3 rounded-2xl border border-stone-200 p-2.5 dark:border-stone-800">
                  <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-white"><DealImage deal={deal} compact /></div>
                  <div className="min-w-0 flex-1">
                    <span className="text-[10px] font-black uppercase tracking-wide text-emerald-700 dark:text-emerald-300">{deal.discountBand} · Nearby</span>
                    <h3 className="mt-1 line-clamp-2 text-sm font-extrabold leading-5 text-[#211238] dark:text-white">{deal.name}</h3>
                    <p className="mt-1 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wide text-violet-700 dark:text-violet-300"><LockKeyhole className="h-3 w-3" /> Exact price + store + aisle</p>
                  </div>
                </article>
              ))}
            </div>
            <footer className="border-t border-stone-100 bg-violet-50 p-3 dark:border-stone-800 dark:bg-violet-500/10">
              <button type="button" onClick={() => onSeePlans(preview.zipCode)} className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-violet-600 px-5 text-sm font-black text-white transition hover:bg-violet-700">
                {audience === "guest" ? "Create account & see plans" : "See plans for exact details"}
                <ArrowRight className="h-4 w-4" />
              </button>
            </footer>
          </section>
        </div>,
        document.body,
      )}
    </>
  );
}
